import type { Availability } from '@/types';
import { g } from '@/lib/genderCopy';

// Gendered availability copy (Founder 30/09/26 gender engine): he_professionals.gender m/f → "פנוי/פנויה היום"; unknown → the
// neutral "זמינות היום" — never a slash form. The ONE table for every card, the pro page and the portal preview.
export type AvailWord = 'now' | 'today' | 'tomorrow' | 'week' | 'unavailable';
const AVAIL_TEXT: Record<AvailWord, { m: string; f: string; u: string }> = {
  now: { m: 'פנוי כעת', f: 'פנויה כעת', u: 'זמינות כעת' },
  today: { m: 'פנוי היום', f: 'פנויה היום', u: 'זמינות היום' },
  tomorrow: { m: 'פנוי מחר', f: 'פנויה מחר', u: 'זמינות מחר' },
  week: { m: 'פנוי השבוע', f: 'פנויה השבוע', u: 'זמינות השבוע' },
  unavailable: { m: 'לא פנוי', f: 'לא פנויה', u: 'אין זמינות כרגע' },
};
export const availLabel = (status: AvailWord, gender?: string | null) => g(gender, AVAIL_TEXT[status]);
/** "זמין" / "זמינה" / "זמינות" — "זמין ביום רביעי", "זמינות מ-05/10". */
export const availableWord = (gender?: string | null) => g(gender, { m: 'זמין', f: 'זמינה', u: 'זמינות' });

export const availabilityConfig: Record<Availability, { label: string; color: string; bg: string; dot: string; priority: number }> = {
  now:         { label: AVAIL_TEXT.now.u,    color: 'text-white',         bg: 'bg-green-500 border-green-600',      dot: 'bg-white',       priority: 1 },
  today:       { label: AVAIL_TEXT.today.u,   color: 'text-green-800',     bg: 'bg-green-100 border-green-400',      dot: 'bg-green-500',   priority: 2 },
  tomorrow:    { label: AVAIL_TEXT.tomorrow.u,    color: 'text-yellow-800',    bg: 'bg-yellow-100 border-yellow-400',    dot: 'bg-yellow-500',  priority: 3 },
  week:        { label: AVAIL_TEXT.week.u,  color: 'text-orange-800',    bg: 'bg-orange-100 border-orange-400',    dot: 'bg-orange-500',  priority: 4 },
  unavailable: { label: AVAIL_TEXT.unavailable.u,     color: 'text-red-700',       bg: 'bg-red-50 border-red-300',           dot: 'bg-red-400',     priority: 5 },
};

// PER-PRO AVAILABILITY (Founder 2026-09-28: "the pro can set 'not available in the next X days' or a date he'll be available
// again, and the hours he is shown as available on the site — default 08:00–19:00 — synced with how the site shows him").
// ONE function for the site cards (CategoryProCard), the pro page, lead matching and the portal's live preview.
// Shabbat-aware (unchanged rule, 2026-07-12): Saturday → "פנוי/ה מחר"; Friday after 15:00 (or his own end, if earlier) →
// "פנוי/ה השבוע" — never imply availability during Shabbat.
export const DEFAULT_HOURS = { from: '08:00', to: '19:00' } as const;
export type ProAvailInput = { availableFrom?: string | null; hoursFrom?: string | null; hoursTo?: string | null; gender?: string | null };
export type ProAvailStatus = 'today' | 'tomorrow' | 'week' | 'from';
export type ProAvail = { status: ProAvailStatus; label: string };

function ilParts(now: Date) {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short' }).formatToParts(now).map(x => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, minutes: Number(p.hour) * 60 + Number(p.minute), weekday: String(p.weekday) };
}
const toMin = (hhmm: string | null | undefined, dflt: string) => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '')) || /^(\d{1,2}):(\d{2})$/.exec(dflt)!; return Number(m[1]) * 60 + Number(m[2]); };
/** YYYY-MM-DD of Israel "today + n days". */
export function ilDatePlus(n: number, now = new Date()): string { return ilParts(new Date(now.getTime() + n * 86400e3)).date; }
/** "05/10" for a YYYY-MM-DD date. */
export const ddmm = (iso: string) => { const [, m, d] = iso.split('-'); return `${d}/${m}`; };
// Hebrew weekday (Founder 2026-09-28: "to look local — write the weekday in Hebrew, in availability and in the portal")
export const WEEKDAYS_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const dow = (iso: string) => new Date(`${iso}T12:00:00Z`).getUTCDay(); // calendar weekday of a YYYY-MM-DD date
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400e3);
/** "יום רביעי" for a YYYY-MM-DD date. */
export const weekdayHe = (iso: string) => `יום ${WEEKDAYS_HE[dow(iso)]}`;
/** Portal wording: "יום רביעי, 01/10". */
export const dayAndDate = (iso: string) => `${weekdayHe(iso)}, ${ddmm(iso)}`;

export function proAvailability(a: ProAvailInput = {}, now = new Date()): ProAvail {
  try {
    const { date, minutes, weekday } = ilParts(now);
    const gd = a.gender, av = availableWord(gd), L = (s: AvailWord) => availLabel(s, gd);
    const from = a.availableFrom ? String(a.availableFrom).slice(0, 10) : null;
    if (from && from > date) {
      // a return on Shabbat is shown as Sunday (never imply availability on Shabbat)
      const back = dow(from) === 6 ? ilDatePlus(dayDiff(date, from) + 1, now) : from;
      const d = dayDiff(date, back), todayDow = dow(date);
      if (d === 1) return { status: 'tomorrow', label: L('tomorrow') };
      if (d <= 6) return { status: 'from', label: `${av} ב${weekdayHe(back)}` };                    // the coming 7 days: the day's name
      if (d <= 13 - todayDow) return { status: 'from', label: `${av} בשבוע הבא` };             // next Sunday–Friday
      return { status: 'from', label: `${av} מ-${ddmm(back)}` };                               // later: the date (portal shows the day)
    }
    const end = toMin(a.hoursTo, DEFAULT_HOURS.to);
    if (weekday === 'Sat') return { status: 'tomorrow', label: L('tomorrow') };
    if (weekday === 'Fri') return minutes < Math.min(end, 15 * 60) ? { status: 'today', label: L('today') } : { status: 'week', label: L('week') };
    return minutes < end ? { status: 'today', label: L('today') } : { status: 'tomorrow', label: L('tomorrow') };
  } catch {
    return { status: 'today', label: availLabel('today', a.gender) };
  }
}
/** Not bookable now (a future "available from" date) — used to rank last and to skip automatic lead matching. */
export const isAwayUntilLater = (a: ProAvailInput, now = new Date()) => !!a.availableFrom && String(a.availableFrom).slice(0, 10) > ilParts(now).date;

// Global fallback (no per-pro data): the default hours for everyone — kept for callers that have no pro.
export function israelAvailStatus(): 'today' | 'tomorrow' | 'week' {
  const s = proAvailability({}).status;
  return s === 'from' ? 'week' : s;
}
